/**
 * Design token system (NOS-150, spec: docs/specs/BUILDER_BEST_IN_CLASS.md §A1).
 *
 * Shared contract for SPA + Astro public renderers (NOS-247 / M22).
 * Stored tokens live in org_customizations.design_tokens (JSONB) and hold only
 * user choices. Derived values (tints, shades, contrast-safe text colors) are
 * computed here at resolve time so palette changes always regenerate them.
 *
 * Blocks read --nos-* (and legacy --primary-color / --secondary-color /
 * --font-family during transition), which siteCssVars() emits.
 */

export type TypeScale = "compact" | "regular" | "display"
export type SectionSpacing = "compact" | "regular" | "airy"
export type Radius = "sharp" | "soft" | "round"
export type ButtonStyle = "solid" | "outline" | "pill"
export type Elevation = "flat" | "soft" | "layered"

/** Shape persisted in org_customizations.design_tokens. All fields optional. */
export interface StoredDesignTokens {
  colors?: {
    primary?: string
    secondary?: string
    accent?: string
  }
  typography?: {
    headingFamily?: string
    bodyFamily?: string
    scale?: TypeScale
    headingWeight?: number
    headingCase?: "none" | "uppercase"
  }
  spacing?: {
    sectionY?: SectionSpacing
    unit?: number
  }
  shape?: {
    radius?: Radius
    buttonStyle?: ButtonStyle
  }
  elevation?: Elevation
}

/** Fully-resolved token set. Every value present; derived colors computed. */
export interface ResolvedDesignTokens {
  colors: {
    primary: string
    secondary: string
    accent: string
    primaryTint: string
    primaryShade: string
    surface: string
    surfaceAlt: string
    text: string
    textMuted: string
    textOnPrimary: string
  }
  typography: {
    headingFamily: string
    bodyFamily: string
    scale: TypeScale
    headingWeight: number
    headingCase: "none" | "uppercase"
  }
  spacing: {
    sectionY: SectionSpacing
    unit: number
  }
  shape: {
    radius: Radius
    buttonStyle: ButtonStyle
  }
  elevation: Elevation
}

export const DEFAULT_PRIMARY = "#3b82f6"
export const DEFAULT_SECONDARY = "#1e40af"
export const DEFAULT_FONT = "Inter, sans-serif"

const LIGHT_TEXT = "#ffffff"
const DARK_TEXT = "#0f172a"
const MUTED_TEXT = "#64748b"
const SURFACE = "#ffffff"
const SURFACE_ALT = "#f7f8fa"

// --- Color math (no deps) ---------------------------------------------------

interface Hsl {
  h: number
  s: number
  l: number
}

const HEX_RE = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i

function normalizeHex(hex: string): string | null {
  const value = hex.trim()
  if (!HEX_RE.test(value)) return null
  if (value.length === 4) {
    return `#${value[1]}${value[1]}${value[2]}${value[2]}${value[3]}${value[3]}`.toLowerCase()
  }
  return value.toLowerCase()
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const n = parseInt(hex.slice(1), 16)
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 }
}

function hexToHsl(hex: string): Hsl {
  const { r, g, b } = hexToRgb(hex)
  const rn = r / 255
  const gn = g / 255
  const bn = b / 255
  const max = Math.max(rn, gn, bn)
  const min = Math.min(rn, gn, bn)
  const l = (max + min) / 2
  if (max === min) return { h: 0, s: 0, l }
  const d = max - min
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
  let h: number
  if (max === rn) h = ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6
  else if (max === gn) h = ((bn - rn) / d + 2) / 6
  else h = ((rn - gn) / d + 4) / 6
  return { h, s, l }
}

function hslToHex({ h, s, l }: Hsl): string {
  const hue2rgb = (p: number, q: number, t: number) => {
    let tn = t
    if (tn < 0) tn += 1
    if (tn > 1) tn -= 1
    if (tn < 1 / 6) return p + (q - p) * 6 * tn
    if (tn < 1 / 2) return q
    if (tn < 2 / 3) return p + (q - p) * (2 / 3 - tn) * 6
    return p
  }
  let r: number, g: number, b: number
  if (s === 0) {
    r = g = b = l
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s
    const p = 2 * l - q
    r = hue2rgb(p, q, h + 1 / 3)
    g = hue2rgb(p, q, h)
    b = hue2rgb(p, q, h - 1 / 3)
  }
  const toHex = (v: number) => Math.round(v * 255).toString(16).padStart(2, "0")
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

function withLightness(hex: string, l: number): string {
  const hsl = hexToHsl(hex)
  return hslToHex({ ...hsl, l })
}

function relativeLuminance(hex: string): number {
  const { r, g, b } = hexToRgb(hex)
  const chan = (v: number) => {
    const c = v / 255
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  }
  return 0.2126 * chan(r) + 0.7152 * chan(g) + 0.0722 * chan(b)
}

/** WCAG contrast ratio between two hex colors (1–21). */
export function contrastRatio(a: string, b: string): number {
  const ha = normalizeHex(a)
  const hb = normalizeHex(b)
  if (!ha || !hb) return 1
  const la = relativeLuminance(ha)
  const lb = relativeLuminance(hb)
  const [hi, lo] = la >= lb ? [la, lb] : [lb, la]
  return (hi + 0.05) / (lo + 0.05)
}

// --- Resolution --------------------------------------------------------------

/** Legacy columns consulted when design_tokens is missing a value. */
export interface LegacyCustomizations {
  primary_color?: string | null
  secondary_color?: string | null
  font_family?: string | null
  heading_font?: string | null
  body_font?: string | null
  font_size?: "small" | "medium" | "large" | null
  design_tokens?: StoredDesignTokens | null
}

function legacyScale(fontSize: LegacyCustomizations["font_size"]): TypeScale {
  if (fontSize === "small") return "compact"
  if (fontSize === "large") return "display"
  return "regular"
}

function safeHex(candidate: string | undefined | null, fallback: string): string {
  if (!candidate) return fallback
  return normalizeHex(candidate) ?? fallback
}

/**
 * Resolve stored tokens + legacy columns into a complete token set.
 * Precedence per field: stored token → legacy column → default.
 * Orgs with no design_tokens resolve to exactly what the legacy columns render today.
 */
export function resolveTokens(
  stored: StoredDesignTokens | null | undefined,
  legacy?: LegacyCustomizations | null
): ResolvedDesignTokens {
  const primary = safeHex(stored?.colors?.primary, safeHex(legacy?.primary_color, DEFAULT_PRIMARY))
  const secondary = safeHex(
    stored?.colors?.secondary,
    safeHex(legacy?.secondary_color, DEFAULT_SECONDARY)
  )
  // Accent defaults to secondary so legacy orgs gain no new hue until they pick one.
  const accent = safeHex(stored?.colors?.accent, secondary)

  const legacyFont = legacy?.font_family || DEFAULT_FONT
  const headingFamily =
    stored?.typography?.headingFamily || legacy?.heading_font || legacyFont
  const bodyFamily = stored?.typography?.bodyFamily || legacy?.body_font || legacyFont

  const textOnPrimary = contrastRatio(primary, LIGHT_TEXT) >= 3 ? LIGHT_TEXT : DARK_TEXT

  return {
    colors: {
      primary,
      secondary,
      accent,
      primaryTint: withLightness(primary, 0.93),
      primaryShade: withLightness(primary, Math.max(hexToHsl(primary).l * 0.6, 0.08)),
      surface: SURFACE,
      surfaceAlt: SURFACE_ALT,
      text: DARK_TEXT,
      textMuted: MUTED_TEXT,
      textOnPrimary,
    },
    typography: {
      headingFamily,
      bodyFamily,
      scale: stored?.typography?.scale ?? legacyScale(legacy?.font_size),
      headingWeight: stored?.typography?.headingWeight ?? 700,
      headingCase: stored?.typography?.headingCase ?? "none",
    },
    spacing: {
      sectionY: stored?.spacing?.sectionY ?? "regular",
      unit: stored?.spacing?.unit ?? 8,
    },
    shape: {
      radius: stored?.shape?.radius ?? "soft",
      buttonStyle: stored?.shape?.buttonStyle ?? "solid",
    },
    elevation: stored?.elevation ?? "soft",
  }
}

/**
 * Recompute the legacy-backed token fields (colors, typography) from an org's
 * legacy columns, preserving token-only fields (accent, spacing, shape,
 * elevation). Called by the customization store whenever a legacy column is
 * updated so stored tokens never go stale — stored values take precedence over
 * legacy at resolve time, so a stale stored color would otherwise win.
 */
export function syncStoredTokensWithLegacy(cust: LegacyCustomizations): StoredDesignTokens {
  const stored = (cust.design_tokens as StoredDesignTokens | null | undefined) ?? {}
  const font = cust.font_family || DEFAULT_FONT
  return {
    ...stored,
    colors: {
      ...stored.colors,
      primary: safeHex(cust.primary_color, DEFAULT_PRIMARY),
      secondary: safeHex(cust.secondary_color, DEFAULT_SECONDARY),
    },
    typography: {
      ...stored.typography,
      headingFamily: cust.heading_font || font,
      bodyFamily: cust.body_font || font,
      scale: legacyScale(cust.font_size),
    },
  }
}

const SCALE_TO_FONT_SIZE: Record<TypeScale, "small" | "medium" | "large"> = {
  compact: "small",
  regular: "medium",
  display: "large",
}

/**
 * Legacy column values implied by a stored token object — the write-through
 * mirror of syncStoredTokensWithLegacy, used when tokens are edited directly
 * (Brand Panel v2) so pre-token readers stay correct during the transition.
 */
export interface LegacyTokenColumns {
  primary_color?: string
  secondary_color?: string
  heading_font?: string
  body_font?: string
  font_size?: "small" | "medium" | "large"
}

export function legacyColumnsFromTokens(stored: StoredDesignTokens): LegacyTokenColumns {
  const cols: LegacyTokenColumns = {}
  if (stored.colors?.primary) cols.primary_color = stored.colors.primary
  if (stored.colors?.secondary) cols.secondary_color = stored.colors.secondary
  if (stored.typography?.headingFamily) cols.heading_font = stored.typography.headingFamily
  if (stored.typography?.bodyFamily) cols.body_font = stored.typography.bodyFamily
  if (stored.typography?.scale) cols.font_size = SCALE_TO_FONT_SIZE[stored.typography.scale]
  return cols
}

// --- CSS variable emission ----------------------------------------------------

/** Responsive so "regular" matches the render layer's previous py-12→py-24 rhythm. */
const SECTION_Y_PX: Record<SectionSpacing, string> = {
  compact: "clamp(32px, 5vw, 48px)",
  regular: "clamp(48px, 7vw, 96px)",
  airy: "clamp(80px, 10vw, 144px)",
}

const RADIUS_PX: Record<Radius, string> = {
  sharp: "0px",
  soft: "12px",
  round: "24px",
}

const ELEVATION_SHADOW: Record<Elevation, string> = {
  flat: "none",
  soft: "0 1px 3px rgb(0 0 0 / 0.08)",
  layered: "0 4px 16px rgb(0 0 0 / 0.12)",
}

const SCALE_BASE_PX: Record<TypeScale, string> = {
  compact: "14px",
  regular: "16px",
  display: "18px",
}

/** Emit the full --nos-* variable set for a resolved token object. */
export function tokensToCssVars(tokens: ResolvedDesignTokens): Record<string, string> {
  const isOutline = tokens.shape.buttonStyle === "outline"
  const buttonRadius =
    tokens.shape.buttonStyle === "pill" ? "9999px" : RADIUS_PX[tokens.shape.radius]
  return {
    "--nos-color-primary": tokens.colors.primary,
    "--nos-color-secondary": tokens.colors.secondary,
    "--nos-color-accent": tokens.colors.accent,
    "--nos-color-primary-tint": tokens.colors.primaryTint,
    "--nos-color-primary-shade": tokens.colors.primaryShade,
    "--nos-color-surface": tokens.colors.surface,
    "--nos-color-surface-alt": tokens.colors.surfaceAlt,
    "--nos-color-text": tokens.colors.text,
    // NOS-180 (bench design rule): section headings are dark neutral, never
    // the brand hue — brand color lands on CTAs/accents as a deliberate splash.
    "--nos-heading-color": tokens.colors.text,
    "--nos-color-text-muted": tokens.colors.textMuted,
    "--nos-color-text-on-primary": tokens.colors.textOnPrimary,
    "--nos-font-heading": tokens.typography.headingFamily,
    "--nos-font-body": tokens.typography.bodyFamily,
    "--nos-heading-weight": String(tokens.typography.headingWeight),
    "--nos-heading-case": tokens.typography.headingCase,
    "--nos-font-size-base": SCALE_BASE_PX[tokens.typography.scale],
    "--nos-space-section-y": SECTION_Y_PX[tokens.spacing.sectionY],
    "--nos-space-unit": `${tokens.spacing.unit}px`,
    "--nos-radius": RADIUS_PX[tokens.shape.radius],
    "--nos-elevation-shadow": ELEVATION_SHADOW[tokens.elevation],
    // Button feel (solid | outline | pill). Two contexts: buttons on light
    // surfaces read --nos-button-*, buttons sitting on primary/gradient
    // backgrounds read --nos-button-on-primary-* (inverted fill).
    "--nos-button-radius": buttonRadius,
    "--nos-button-bg": isOutline ? "transparent" : tokens.colors.primary,
    "--nos-button-color": isOutline ? tokens.colors.primary : tokens.colors.textOnPrimary,
    "--nos-button-border-color": isOutline ? tokens.colors.primary : "transparent",
    "--nos-button-on-primary-bg": isOutline ? "transparent" : "#ffffff",
    "--nos-button-on-primary-color": isOutline ? "#ffffff" : tokens.colors.primary,
    "--nos-button-on-primary-border-color": isOutline ? "#ffffff" : "transparent",
  }
}

/**
 * One-call helper for site roots (public pages and builder canvas): resolves
 * customizations (design_tokens JSONB + legacy columns) and returns the --nos-*
 * set PLUS the legacy three variables blocks still read during the transition.
 */
export function siteCssVars(
  customizations: LegacyCustomizations | null | undefined
): Record<string, string> {
  const tokens = resolveTokens(
    (customizations?.design_tokens as StoredDesignTokens | null | undefined) ?? null,
    customizations ?? null
  )
  const bodyFont =
    (customizations?.body_font || customizations?.font_family) ?? DEFAULT_FONT
  return {
    ...tokensToCssVars(tokens),
    "--primary-color": tokens.colors.primary,
    "--secondary-color": tokens.colors.secondary,
    "--font-family": bodyFont,
  }
}
